import type { FieldTree, SchemaPathTree } from '@angular/forms/signals';
import { readonly, validateStandardSchema } from '@angular/forms/signals';

import type {
    BlockCommonProps,
    BlockEntity,
    DefaultBlock,
    Device,
    Side,
    SpecificBlock,
    ZeroBlock,
} from '@sites/entity-core';
import {
    blockDefaultV0Schema,
    blockSpecificV0Schema,
    blockZeroV0Schema,
    compactBlockProps,
    compactDisplayCondition,
    createShapeDivider,
} from '@sites/entity-core';

import type { BaseEntityFormOptions } from '../../core/create-base-entity-form';
import { createBaseEntityForm } from '../../core/create-base-entity-form';
import { mergeAccessors } from '../../core/merge-accessors';
import { writeDevice } from '../../core/write-device';
import {
    createBackgroundAccessors,
    createBackgroundBlockLayerAccessors,
} from '../../features/background/background-accessors';
import { createBorderAccessors } from '../../features/border/border-accessors';
import { withSpacingSides, withSpacingUnit } from '../../mixins/spacing/spacing-updaters';

export type BlockFormModel = BlockEntity;
export type BlockDefaultProps = DefaultBlock['props'];
export type BlockSpecificProps = SpecificBlock['props'];
export type BlockZeroProps = ZeroBlock['props'];

export type BlockSpacingValue = BlockDefaultProps['padding']['desktop']['value'];
export type BlockSpacingUnit = BlockDefaultProps['padding']['desktop']['unit'];
export type BlockBorderSide = NonNullable<BlockDefaultProps['border']['top']>['desktop'];
export type BlockBackgroundColor = BlockDefaultProps['background']['color']['desktop'];
export type BlockBackgroundFile = BlockDefaultProps['background']['image']['desktop'];
export type BlockShapeDividerValue = BlockDefaultProps['shapeDividers']['top']['desktop'];
export type BlockWidth = NonNullable<BlockDefaultProps['width']>;
export type BlockHeight = NonNullable<BlockDefaultProps['height']['desktop']>;
export type BlockDisplayCondition = DefaultBlock['meta']['displayCondition'];
export type BlockMeta = DefaultBlock['meta'];
export type BlockRole = NonNullable<BlockMeta['role']>;
export type BlockPin = BlockDefaultProps['pin'];

export type BlockDefaultForm = ReturnType<typeof createBlockDefaultForm>;
export type BlockSpecificForm = ReturnType<typeof createBlockSpecificForm>;
export type BlockZeroForm = ReturnType<typeof createBlockZeroForm>;
export type BlockForm = BlockDefaultForm | BlockSpecificForm | BlockZeroForm;

/** Which edge a shape divider is drawn along. */
export type BlockShapeDividerEdge = 'top' | 'bottom';

type BlockVariant = 'default' | 'specific' | 'zero';

/**
 * The slices of a block the two shared accessor groups touch — the envelope all
 * three subtypes have, and the box all three paint.
 *
 * Concrete, not generic. `FieldTree<T>` is a conditional type, so through a
 * bounded generic TypeScript defers it and `_raw.props` stops being navigable;
 * naming the shared shape is what lets the getters below hand back a real leaf
 * type instead of `any`. The narrowing cast at each call site is the price, and
 * it buys a typed accessor for every consumer.
 */
type BlockEnvelopeLike = Pick<DefaultBlock, 'children' | 'meta'>;
type BlockBoxLike = { props: BlockCommonProps };

type EnvelopeBase = { _raw: FieldTree<BlockEnvelopeLike> };
type BoxBase = { _raw: FieldTree<BlockBoxLike> };

const DEVICES: readonly Device[] = ['desktop', 'tablet', 'mobile'];

/**
 * `id` and the version tags are infrastructure: the editor never lets the user
 * mutate them. A block has no `parentId` — it sits directly on the page — which
 * is the one place this differs from the other entities' readonly list.
 */
/** Same story as the accessors: the schema path is a conditional type too. */
const configureBlockReadonly = (schemaPath: SchemaPathTree<BlockEntity>): void => {
    readonly(schemaPath.id);
    readonly(schemaPath.entityType);
    readonly(schemaPath.entitySubType);
    readonly(schemaPath.version);
};

/**
 * Envelope accessors all three subtypes share.
 *
 * The sharing keys in `meta` are readable but have no setter: whether a block
 * is the site-wide header is decided by the host's share/unshare actions, never
 * on the block's own panel.
 */
const createCommonAccessors = (base: EnvelopeBase) => {
    const { _raw } = base;

    return {
        /** Child ids — the host owns adds/removes; the form only reorders. */
        get children() {
            return _raw.children;
        },
        setChildren: (value: { id: string; schema: string }[]) => {
            _raw.children().value.set(value);
        },
        updateChildren: (updater: (current: unknown[]) => unknown[]) => {
            _raw.children().value.update(updater);
        },

        /** Who the block is and where it came from. */
        get meta() {
            return _raw.meta;
        },

        /** The element id the block renders with, so a link can point at it. */
        get displayId() {
            return _raw.meta.displayId;
        },
        setDisplayId: (value: string | null) => {
            _raw.meta.displayId().value.set(value);
        },

        /** When the block shows on the page — edited on its own sub-panel. */
        get displayCondition() {
            return _raw.meta.displayCondition;
        },
        setDisplayCondition: (value: BlockDisplayCondition) => {
            // A rule that shows the block to everyone is stored as `null`.
            _raw.meta.displayCondition().value.set(compactDisplayCondition(value));
        },
    };
};

/**
 * The settings every block subtype carries. Kept as one group rather than folded
 * into each factory: the three panels edit the same box — padding, contour,
 * background, dividers — and only differ in what they add on top.
 */
const createBoxAccessors = (base: BoxBase) => {
    const { _raw } = base;

    // The background is the shared feature; the block adds its video and its
    // per-breakpoint scroll effect on top.
    const featureAccessors = mergeAccessors(
        mergeAccessors(
            createBackgroundAccessors(() => _raw.props.background),
            createBackgroundBlockLayerAccessors(() => _raw.props.background)
        ),
        createBorderAccessors(() => _raw.props.border)
    );

    return mergeAccessors(featureAccessors, {
        /** `null` is the content column every block gets by default. */
        get width() {
            return _raw.props.width;
        },
        setWidth: (value: BlockWidth | null) => {
            _raw.props.width().value.set(value);
        },

        /**
         * How tall the block is, or `null` when its contents decide. A canvas
         * reads it as exact and writes one breakpoint at a time; an ordinary
         * block reads it as a minimum and is the same on every screen, so it
         * uses {@link setHeightEverywhere}.
         */
        get height() {
            return _raw.props.height;
        },
        setHeight: (value: BlockHeight | null, device: Device = 'desktop') => {
            writeDevice(_raw.props.height, device, () => value);
        },
        setHeightEverywhere: (value: BlockHeight | null) => {
            DEVICES.forEach((device) => writeDevice(_raw.props.height, device, () => value));
        },

        /** Sticks the block to the top or the bottom while the page scrolls. */
        get pin() {
            return _raw.props.pin;
        },
        setPin: (value: BlockPin) => {
            _raw.props.pin().value.set(value);
        },

        /** Write desktop style edits through to the tablet and mobile tabs. */
        get syncWithDesktop() {
            return _raw.props.syncWithDesktop;
        },
        setSyncWithDesktop: (value: boolean) => {
            _raw.props.syncWithDesktop().value.set(value);
        },

        /**
         * One switch per breakpoint rather than a responsive value: all three are
         * read at once to decide where the block renders at all.
         */
        get hidden() {
            return _raw.props.hidden;
        },
        setHidden: (device: Device, value: boolean) => {
            _raw.props.hidden[device]().value.set(value);
        },

        // --- Responsive leaves ----------------------------------------------
        // Each takes the breakpoint it writes, so the panel's three tabs share
        // one setter instead of one per device.

        get padding() {
            return _raw.props.padding;
        },
        /** The four sides live in one leaf, so a single-side edit merges into it. */
        setPadding: (patch: Partial<BlockSpacingValue>, device: Device = 'desktop') => {
            writeDevice(_raw.props.padding, device, withSpacingSides(patch));
        },
        setPaddingUnit: (unit: BlockSpacingUnit, device: Device = 'desktop') => {
            writeDevice(_raw.props.padding, device, withSpacingUnit(unit));
        },

        /**
         * The decorative edge. Seven fields that are only meaningful together, so
         * the whole leaf is patched at once — no control edits one of them alone.
         */
        get shapeDividers() {
            return _raw.props.shapeDividers;
        },
        setShapeDivider: (
            edge: BlockShapeDividerEdge,
            patch: Partial<BlockShapeDividerValue>,
            device: Device = 'desktop'
        ) => {
            writeDevice(_raw.props.shapeDividers[edge], device, (current) => {
                // A divider that was off keeps only its switch; switching it on seeds the rest.
                const isSwitchingOn = patch.isEnabled && current?.view === undefined;

                return { ...(isSwitchingOn ? createShapeDivider() : {}), ...current, ...patch };
            });
        },

        /**
         * Stores only what says something — see `compactBlockProps`. The panels call
         * it at the end of a write, inside the same transaction, so the one dispatch
         * already carries the compacted props.
         */
        compact: () => {
            _raw.props().value.update((props: BlockCommonProps) => compactBlockProps(props));
        },
    });
};

export function createBlockDefaultForm(options?: BaseEntityFormOptions<DefaultBlock>) {
    const base = createBaseEntityForm<DefaultBlock>((schemaPath) => {
        configureBlockReadonly(schemaPath);
        validateStandardSchema(schemaPath, blockDefaultV0Schema);
    }, options);

    // `mergeAccessors`, not object spread: spreading would call the getters while
    // the model is still the empty seed.
    return mergeAccessors(
        mergeAccessors({ ...base }, createCommonAccessors(base as EnvelopeBase)),
        createBoxAccessors(base as BoxBase)
    );
}

/**
 * The header/footer block. On top of the shared box it styles the burger bar it
 * collapses into on a narrow screen, and it hides individual widgets by id
 * rather than whole rows.
 */
export function createBlockSpecificForm(options?: BaseEntityFormOptions<SpecificBlock>) {
    const base = createBaseEntityForm<SpecificBlock>((schemaPath) => {
        configureBlockReadonly(schemaPath);
        validateStandardSchema(schemaPath, blockSpecificV0Schema);
    }, options);

    const { _raw } = base;

    return mergeAccessors(
        mergeAccessors(
            mergeAccessors({ ...base }, createCommonAccessors(base as EnvelopeBase)),
            createBoxAccessors(base as BoxBase)
        ),
        {
            /** The burger glyph: `EHorizontalPosition`, `ESize` and a theme colour. */
            get icon() {
                return _raw.props.icon;
            },
            setIcon: (patch: Partial<BlockSpecificProps['icon']>) => {
                _raw.props.icon().value.update((current: BlockSpecificProps['icon']) => ({ ...current, ...patch }));
            },

            get textColor() {
                return _raw.props.textColor;
            },
            setTextColor: (value: string) => {
                _raw.props.textColor().value.set(value);
            },

            /** Where the collapsed menu's contents line up — `L | M | R`. */
            get horizontalPosition() {
                return _raw.props.horizontalPosition;
            },
            setHorizontalPosition: (value: string) => {
                _raw.props.horizontalPosition().value.set(value);
            },

            get hideContentOnMobile() {
                return _raw.props.hideContentOnMobile;
            },
            setHideContentOnMobile: (value: boolean) => {
                _raw.props.hideContentOnMobile().value.set(value);
            },

            get hasSeparator() {
                return _raw.props.hasSeparator;
            },
            setHasSeparator: (value: boolean) => {
                _raw.props.hasSeparator().value.set(value);
            },

            /**
             * Keyed by widget id, so the map grows and shrinks with the header's
             * contents — written through the parent rather than a child node,
             * which would only exist for ids already in the model.
             */
            get hideWidgetsOnMobile() {
                return _raw.props.hideWidgetsOnMobile;
            },
            setWidgetHiddenOnMobile: (widgetId: string, value: boolean) => {
                _raw.props.hideWidgetsOnMobile().value.update((current: Record<string, boolean>) => ({
                    ...current,
                    [widgetId]: value,
                }));
            },
        }
    );
}

/**
 * The free-positioning canvas. Its widgets are placed absolutely, so it owns an
 * explicit height per breakpoint instead of leaving it to its contents.
 */
export function createBlockZeroForm(options?: BaseEntityFormOptions<ZeroBlock>) {
    const base = createBaseEntityForm<ZeroBlock>((schemaPath) => {
        configureBlockReadonly(schemaPath);
        validateStandardSchema(schemaPath, blockZeroV0Schema);
    }, options);

    const { _raw } = base;

    return mergeAccessors(
        mergeAccessors(
            mergeAccessors({ ...base }, createCommonAccessors(base as EnvelopeBase)),
            createBoxAccessors(base as BoxBase)
        ),
        {
            /** Clips whatever a widget pushes past the canvas edge. */
            get hideOverflow() {
                return _raw.props.hideOverflow;
            },
            setHideOverflow: (value: boolean) => {
                _raw.props.hideOverflow().value.set(value);
            },
        }
    );
}

/**
 * Picks the factory from the entity's own subtype. The three variants differ in
 * shape, so the schema has to be chosen at construction — a header block handed
 * to the default factory would validate against a schema missing the burger bar
 * it carries.
 */
export function createBlockForm(options?: BaseEntityFormOptions<BlockEntity>): BlockForm {
    const entity =
        (options as { initialData?: BlockEntity })?.initialData ?? (options?.entity?.() as BlockEntity | undefined);
    const variant: BlockVariant = entity?.entitySubType ?? 'default';

    if (variant === 'specific') {
        return createBlockSpecificForm(options as BaseEntityFormOptions<SpecificBlock>);
    }

    if (variant === 'zero') {
        return createBlockZeroForm(options as BaseEntityFormOptions<ZeroBlock>);
    }

    return createBlockDefaultForm(options as BaseEntityFormOptions<DefaultBlock>);
}
